import type { OptimizerResult, SearchSettings } from '../types/optimizer';
import type { SearchProgress } from './engine';

export const difficultyName = (chartId: string) => ({ '1': 'Normal', '2': 'Hard', '3': 'Advanced' }[chartId.split('_').pop() ?? ''] ?? 'Chart');
export const songIdFromChart = (chartId: string) => chartId.toLowerCase().replace(/_[1-5]$/, '');
export interface LevelSearchResult { chart_id: string; result: OptimizerResult | null; error?: string }
export interface SongSearchResult { song_id: string; levels: LevelSearchResult[]; best: OptimizerResult | null }
export interface SongSearchProgress extends SearchProgress { label?: string }
export type ChartOptimizer = (chartId: string, settings: SearchSettings, options: {
    signal: AbortSignal; onProgress: (progress: SearchProgress) => void;
}) => Promise<OptimizerResult>;

/** Run sequentially so three CPU-intensive searches do not compete on a phone. */
export async function searchSongLevels(chartId: string, settings: SearchSettings, optimize: ChartOptimizer,
    options: { signal: AbortSignal; onProgress: (progress: SongSearchProgress) => void }): Promise<SongSearchResult> {
    const songId = songIdFromChart(chartId);
    const levels: LevelSearchResult[] = [];
    let best: OptimizerResult | null = null;
    for (let level = 1; level <= 3; level++) {
        if (options.signal.aborted) throw new DOMException('Search cancelled', 'AbortError');
        const id = `${songId}_${level}`;
        const label = `${difficultyName(id)} (${level}/3)`;
        options.onProgress({ completed: 0, total: 0, label });
        try {
            const result = await optimize(id, settings, {
                signal: options.signal,
                onProgress: progress => options.onProgress({ ...progress, label }),
            });
            if (options.signal.aborted) throw new DOMException('Search cancelled', 'AbortError');
            if (result.chart_id !== id) throw new Error('Unexpected chart result');
            levels.push({ chart_id: id, result });
            if (result.best && (!best?.best || result.best.score_per_note > best.best.score_per_note)) best = result;
        } catch (err) {
            if (options.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) throw err;
            levels.push({ chart_id: id, result: null, error: err instanceof Error ? err.message : 'Unable to search this level' });
        }
    }
    if (!levels.some(level => level.result)) throw new Error(`Unable to search any level: ${levels.map(level => `${difficultyName(level.chart_id)}: ${level.error}`).join('; ')}`);
    return { song_id: songId, levels, best };
}
